import {
  CONSUMER_CMS_SHOPPING_CART,
  ErrorCodes,
  errorMock,
  getCurrentUserType,
  GetCustomerResponse,
  IFindCallerResponse,
  IGlobalConfigCMS,
  IUserType,
  useAssociateCustomer,
  useBillingAccount,
  useCmsConfig,
  useCustomer,
  useGetCustomer,
  useGetFindCaller,
  useGetPaymean,
  usePatchCustomer,
  usePayment,
  useUpdateProductCharacteristic,
} from '@vfit/consumer/data-access';
import { useEffect, useState } from 'react';
import {
  API,
  computeInverse,
  getFromLocalStorageByKey,
  IFiscalCodeReverse,
} from '@vfit/shared/data-access';
import { LoggerInstance } from '@vfit/shared/data-access';
import { useCheckout } from '../../../iBuyMobile.context';
import { getBSNewMopMobile, getErrorStatusText, getPatchPayload } from './useCustomerFlow.utils';
import { ID_FLOWS } from '../../checkout.constants';
import { IUseCustomerResponse } from './useCustomerFlow.models';
import { handleCRMErrorCodes } from '../../checkout.utils';

/**
 * Customer flow
 * if the user is NEXT ACTIVE call this flow
 * findcaller - getCustomer - familyPlusFlow if is not winback - associatecustomer - payment - billingaccount - paymean - updateproductcharacteristics
 * if the user in Prospect or Logged call
 * patchCustomer/postCustomer - getCustomer - associatecustomer - payment - billingaccount - paymean - updateproductcharacteristics
 * if user is logged
 * familyPlusFlow if is not winback - associatecustomer - payment - billingaccount - paymean - updateproductcharacteristics
 */
export const useCustomerFlow = () => {
  const {
    isLoadingCart,
    isStartCustomerFlow,
    owningData,
    customerFlow,
    currentStepKey,
    campaign,
    isStartCampaign,
    findCaller: findCallerPreOTP,
    product,
    setCustomerFlow,
    setCustomerId: setContextCustomerId,
    setIsStartCustomerFlow,
    setIsStartCampaign,
    setBillingEmail,
    setBillingAddress,
  } = useCheckout();
  const globalConfig = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GLOBAL_CONFIG
  ) as IGlobalConfigCMS;
  const [customerId, setCustomerId] = useState<string>('');
  const isNextActive = !!(owningData.isValid && findCallerPreOTP.verifyOtp);
  const isLogged = getCurrentUserType() === IUserType.LOGGED_USER;
  // region APIs for customer flow
  const {
    data: associateCustomerData,
    error: associateCustomerError,
    mutate: associateCustomerMutate,
    isSuccess: isSuccessAssociateCustomer,
    isError: isErrorAssociateCustomer,
    isLoading: isLoadingAssociateCustomer,
  } = useAssociateCustomer();
  const {
    data: patchCustomerData,
    error: patchCustomerError,
    mutate: patchCustomerMutate,
    isSuccess: isSuccessPatchCustomer,
    isError: isErrorPatchCustomer,
    isLoading: isLoadingPatchCustomer,
  } = usePatchCustomer();
  const {
    data: postCustomerData,
    error: postCustomerError,
    mutate: postCustomerMutate,
    isSuccess: isSuccessPostCustomer,
    isError: isErrorPostCustomer,
    isLoading: isLoadingPostCustomer,
  } = useCustomer();
  const {
    data: customerData,
    error: customerError,
    isSuccess: isSuccessGetCustomer,
    isError: isErrorGetCustomer,
    isLoading: isLoadingGetCustomer,
    refetch: refetchGetCustomer,
  } = useGetCustomer(customerId, { enabled: false });
  const {
    error: paymentError,
    data: paymentData,
    isSuccess: isSuccessPayment,
    isLoading: isLoadingPayment,
    isError: isErrorPayment,
    refetch: refetchPayment,
  } = usePayment({ enabled: false });
  const {
    error: paymeanError,
    data: paymeanData,
    isSuccess: isSuccessPaymean,
    isLoading: isLoadingPaymean,
    isError: isErrorPaymean,
    refetch: refetchPaymean,
  } = useGetPaymean({ enabled: false }, globalConfig);
  const {
    mutate: mutateUpdateProductCharacteristics,
    error: updateProductCharacteristicsError,
    isError: isErrorUpdateProductCharacteristics,
    isSuccess: isSuccessUpdateProductCharacteristics,
    isLoading: isLoadingUpdateProductCharacteristics,
  } = useUpdateProductCharacteristic();
  const {
    data: billingAccountData,
    error: billingAccountError,
    isLoading: isBillingAccountLoading,
    isError: isBillingAccountError,
    isSuccess: isBillingAccountSuccess,
    refetch: refetchBillingAccount,
  } = useBillingAccount(
    {
      enabled: false,
    },
    globalConfig
  );
  const {
    data: findcallerData,
    error: findcallerError,
    isError: isErrorFindcaller,
    isSuccess: isSuccessFindcaller,
    isLoading: isLoadingFindcaller,
    refetch: refetchFindcaller,
  } = useGetFindCaller(
    {
      fiscalCode: owningData?.owningIndividual?.fiscalCode?.toUpperCase(),
      ...(product?.isWinback && { isWinbackFlow: true }),
    },
    {
      enabled: false,
    }
  );

  const isAlreadyDoneAssociateCustomer = () => {
    const alreadyDoneAssociateCustomer = getFromLocalStorageByKey('associateCustomer');
    const shoppingCart = getFromLocalStorageByKey('shoppingCart');
    return !!shoppingCart && !!alreadyDoneAssociateCustomer;
  };

  const checkAssociateCustomer = () => {
    const CUSTOMER_IS_ELIGIBLE_FOR_CAMPAIGN =
      !isLoadingCart && campaign?.campaign?.isSuccess && campaign?.campaign?.isEligible;
    const CUSTOMER_IS_NOT_ELIGIBLE_FOR_CAMPAIGN =
      !isLoadingCart && campaign?.campaign?.isSuccess && !campaign?.campaign?.isEligible;

    const CHECK_CAMPAIGN =
      (campaign?.campaign?.isSuccess && campaign?.campaign?.isRejected) ||
      (campaign?.campaign?.isSuccess &&
        campaign?.accept?.isSuccess &&
        campaign?.accept?.acceptId &&
        campaign?.updateCart?.isSuccess) ||
      campaign?.campaign.isError;

    // If is familyplus MM
    if (product?.isFamilyPlusProduct || product?.forceCampaign) {
      if (CUSTOMER_IS_ELIGIBLE_FOR_CAMPAIGN) {
        if (CHECK_CAMPAIGN && !isAlreadyDoneAssociateCustomer()) {
          associateCustomerMutate();
        }
      }
    } else if (CUSTOMER_IS_ELIGIBLE_FOR_CAMPAIGN || CUSTOMER_IS_NOT_ELIGIBLE_FOR_CAMPAIGN) {
      // If is in campaign but not eligible or campaign is in error or is in campaign but user have rejected the campaign
      // or is in campaign and user accept the campaign
      if (
        CUSTOMER_IS_NOT_ELIGIBLE_FOR_CAMPAIGN ||
        (CHECK_CAMPAIGN && !isAlreadyDoneAssociateCustomer())
      ) {
        associateCustomerMutate();
      }
    }
  };

  useEffect(() => {
    if (isLoadingUpdateProductCharacteristics) {
      setCustomerFlow({
        ...customerFlow,
        resetPatchAndPost,
        updateProductChatacteristics: {
          ...customerFlow.updateProductChatacteristics,
          error: undefined,
          isError: false,
          isSuccess: true,
          isLoading: false,
        },
      });
    } else if (isErrorUpdateProductCharacteristics) {
      setCustomerFlow({
        ...customerFlow,
        resetPatchAndPost,
        updateProductChatacteristics: {
          ...customerFlow.updateProductChatacteristics,
          isLoading: false,
          isSuccess: false,
          isError: true,
          error: errorMock('updateProductChatacteristics', updateProductCharacteristicsError),
        },
      });
    } else if (isSuccessUpdateProductCharacteristics) {
      setCustomerFlow({
        ...customerFlow,
        resetPatchAndPost,
        updateProductChatacteristics: {
          ...customerFlow.updateProductChatacteristics,
          isLoading: false,
          isSuccess: true,
          isError: false,
          error: undefined,
        },
      });
    }
  }, [
    isSuccessUpdateProductCharacteristics,
    isErrorUpdateProductCharacteristics,
    isLoadingUpdateProductCharacteristics,
  ]);

  useEffect(() => {
    if (isLoadingPaymean) {
      setCustomerFlow({
        ...customerFlow,
        paymean: {
          ...customerFlow.paymean,
          error: undefined,
          isLoading: true,
          isSuccess: false,
          isError: false,
        },
      });
    } else if (isErrorPaymean) {
      setCustomerFlow({
        ...customerFlow,
        paymean: {
          ...customerFlow.paymean,
          isSuccess: false,
          isLoading: false,
          isError: true,
          error: errorMock('paymean', paymeanError, undefined, ErrorCodes?.PAYMEAN_ERROR),
        },
      });
    } else if (isSuccessPaymean) {
      setCustomerFlow({
        ...customerFlow,
        paymean: {
          ...customerFlow.paymean,
          error: undefined,
          data: paymeanData,
          isLoading: false,
          isError: false,
          isSuccess: true,
        },
      });
    }
  }, [isErrorPaymean, isLoadingPaymean, isSuccessPaymean]);

  useEffect(() => {
    if (isBillingAccountLoading) {
      setCustomerFlow({
        ...customerFlow,
        billingAccount: {
          ...customerFlow.billingAccount,
          error: undefined,
          isLoading: true,
          isSuccess: false,
          isError: false,
        },
      });
    } else if (isBillingAccountError) {
      setCustomerFlow({
        ...customerFlow,
        billingAccount: {
          ...customerFlow.billingAccount,
          isSuccess: false,
          isError: true,
          isLoading: false,
          error: errorMock(
            'billingAccount',
            billingAccountError,
            undefined,
            ErrorCodes?.BILLING_ACCOUNT_ERROR
          ),
        },
      });
    } else if (isBillingAccountSuccess) {
      setCustomerFlow({
        ...customerFlow,
        paymean: {
          ...customerFlow.paymean,
          isLoading: true,
        },
        billingAccount: {
          ...customerFlow.billingAccount,
          error: undefined,
          isError: false,
          isSuccess: true,
          isLoading: false,
        },
      });
      setBillingEmail(billingAccountData.billDeliveryDetails.emailAddress);
      setBillingAddress({
        ...billingAccountData?.billDeliveryDetails?.physicalAddress,
        latitude: +billingAccountData?.billDeliveryDetails?.physicalAddress?.latitude,
        longitude: +billingAccountData?.billDeliveryDetails?.physicalAddress?.longitude,
      });
      refetchPaymean();
    }
  }, [isBillingAccountError, isBillingAccountLoading, isBillingAccountSuccess]);

  useEffect(() => {
    if (isLoadingPayment) {
      setCustomerFlow({
        ...customerFlow,
        payment: {
          ...customerFlow.payment,
          error: undefined,
          isLoading: true,
          isSuccess: false,
          isError: false,
        },
      });
    } else if (isErrorPayment) {
      setCustomerFlow({
        ...customerFlow,
        payment: {
          ...customerFlow.payment,
          isSuccess: false,
          isLoading: false,
          isError: true,
          error: errorMock('payment', paymentError, undefined, ErrorCodes?.PAYMENT_ERROR),
        },
      });
    } else if (isSuccessPayment) {
      const newMopPaymentData = getBSNewMopMobile(paymentData, globalConfig);
      setCustomerFlow({
        ...customerFlow,
        billingAccount: {
          ...customerFlow.billingAccount,
          isLoading: true,
        },
        payment: {
          ...customerFlow.payment,
          error: undefined,
          data: newMopPaymentData,
          isLoading: false,
          isError: false,
          isSuccess: true,
        },
      });
      refetchBillingAccount();
    }
  }, [isErrorPayment, isLoadingPayment, isSuccessPayment]);

  useEffect(() => {
    if (isLoadingAssociateCustomer) {
      setCustomerFlow({
        ...customerFlow,
        resetPatchAndPost,
        associateCustomer: {
          ...customerFlow.associateCustomer,
          isLoading: true,
          isSuccess: false,
          isError: false,
          error: undefined,
        },
      });
    } else if (isErrorAssociateCustomer) {
      setCustomerFlow({
        ...customerFlow,
        resetPatchAndPost,
        associateCustomer: {
          ...customerFlow.associateCustomer,
          isLoading: false,
          isSuccess: false,
          isError: true,
          error: errorMock('associateCustomer', associateCustomerError),
        },
      });
    } else if (isSuccessAssociateCustomer) {
      if ((associateCustomerData as any)?.cartValidation?.status === 'blocked') {
        setCustomerFlow({
          ...customerFlow,
          resetPatchAndPost,
          associateCustomer: {
            ...customerFlow.associateCustomer,
            data: associateCustomerData,
            isLoading: false,
            isSuccess: false,
            isError: true,
            error: errorMock(
              'associateCustomer',
              {
                status: '200',
                statusText: getErrorStatusText('200', ErrorCodes.ASSOCIATE_CUSTOMER_BLOCKED),
                url: '',
              },
              undefined,
              ErrorCodes.ASSOCIATE_CUSTOMER_BLOCKED
            ),
          },
        });
      } else {
        setCustomerFlow({
          ...customerFlow,
          resetPatchAndPost,
          payment: {
            ...customerFlow.payment,
            isLoading: true,
          },
          associateCustomer: {
            ...customerFlow.associateCustomer,
            data: associateCustomerData,
            isLoading: false,
            isSuccess: true,
            isError: false,
            error: undefined,
          },
        });
        const customerLocal: GetCustomerResponse[] = getFromLocalStorageByKey('customerData');
        const findCallerInactive: IFindCallerResponse = getFromLocalStorageByKey('findCaller');
        if (customerLocal?.[0]?.id || findCallerInactive?.customerRef?.id)
          setContextCustomerId(customerLocal?.[0].id || findCallerInactive?.customerRef?.id || '');
        mutateUpdateProductCharacteristics(undefined);
        refetchPayment();
      }
    }
  }, [isSuccessAssociateCustomer, isErrorAssociateCustomer, isLoadingAssociateCustomer]);

  useEffect(() => {
    if (customerFlow.acceptedFallbackOffer) {
      setCustomerFlow({
        ...customerFlow,
        associateCustomer: {
          data: undefined,
          error: undefined,
          isSuccess: false,
          isLoading: true,
          isError: false,
        },
        payment: {
          data: undefined,
          error: undefined,
          isSuccess: false,
          isLoading: false,
          isError: false,
        },
        billingAccount: { error: undefined, isSuccess: false, isLoading: false, isError: false },
        paymean: {
          data: undefined,
          error: undefined,
          isSuccess: false,
          isLoading: false,
          isError: false,
        },
        updateProductChatacteristics: {
          error: undefined,
          isSuccess: false,
          isLoading: false,
          isError: false,
        },
      });
      associateCustomerMutate();
    }
  }, [customerFlow.acceptedFallbackOffer]);

  useEffect(() => {
    checkAssociateCustomer();
  }, [campaign?.campaign, campaign?.updateCart, campaign?.accept]);

  useEffect(() => {
    if (isLoadingGetCustomer) {
      setCustomerFlow({
        ...customerFlow,
        resetPatchAndPost,
        customerInfo: {
          ...customerFlow.customerInfo,
          isLoading: true,
          isSuccess: false,
          isError: false,
          error: undefined,
        },
      });
    } else if (isErrorGetCustomer) {
      setCustomerFlow({
        ...customerFlow,
        resetPatchAndPost,
        customerInfo: {
          ...customerFlow.customerInfo,
          isLoading: false,
          isSuccess: false,
          isError: true,
          error: errorMock('customerInfo', customerError),
        },
      });
    } else if (isSuccessGetCustomer) {
      const alreadyDoneAssociateCustomer = getFromLocalStorageByKey('associateCustomer');
      if (alreadyDoneAssociateCustomer) {
        setCustomerFlow({
          ...customerFlow,
          resetPatchAndPost,
          customerInfo: {
            ...customerFlow.customerInfo,
            data: customerData?.[0],
            isLoading: false,
            isSuccess: true,
            isError: false,
            error: undefined,
          },
        });
      } else {
        setCustomerFlow({
          ...customerFlow,
          resetPatchAndPost,
          associateCustomer: {
            isLoading: true,
            isSuccess: false,
            isError: false,
            data: undefined,
            error: undefined,
          },
          customerInfo: {
            ...customerFlow.customerInfo,
            data: customerData?.[0],
            isLoading: false,
            isSuccess: true,
            isError: false,
            error: undefined,
          },
        });
        // if is next active or logged and not is winback start family plus flow
        // else associate customer
        const START_CAMPAIGN =
          (isNextActive || isLogged) &&
          !isStartCampaign &&
          !product?.isWinback &&
          ((!product?.isFamilyPlusProduct && product?.familyPlusId) ||
            product?.isFamilyPlusProduct ||
            product?.forceCampaign);
        if (START_CAMPAIGN) {
          setIsStartCampaign(true);
        } else if (
          !isAlreadyDoneAssociateCustomer() &&
          !isLoadingCart &&
          !product?.isFamilyPlusProduct &&
          !product?.forceCampaign
        ) {
          associateCustomerMutate();
        }
      }
    }
  }, [isSuccessGetCustomer, isErrorGetCustomer, isLoadingGetCustomer, isLoadingCart]);

  useEffect(() => {
    if (customerId) {
      const alreadyHaveCustomerData = getFromLocalStorageByKey('customerData');
      if (!alreadyHaveCustomerData) refetchGetCustomer();
    }
  }, [customerId]);

  useEffect(() => {
    if (isLoadingPostCustomer) {
      setCustomerFlow({
        ...customerFlow,
        resetPatchAndPost,
        postCustomer: {
          ...customerFlow.postCustomer,
          isLoading: true,
          isSuccess: false,
          isError: false,
          error: undefined,
        },
      });
    } else if (isErrorPostCustomer) {
      const headerErrorCode = postCustomerError.headers?.get('ERR-errorCode') || '';
      const backendError = postCustomerError.headers?.get('ERR-backendErrorCode') || '';
      const backendMessage = postCustomerError.headers?.get('ERR-backendErrorMessage') || '';
      const errorCode =
        headerErrorCode === ErrorCodes.DIGITAL_CRM_CODE
          ? handleCRMErrorCodes(backendError)
          : headerErrorCode;
      setCustomerFlow({
        ...customerFlow,
        resetPatchAndPost,
        postCustomer: {
          ...customerFlow.postCustomer,
          isLoading: false,
          isSuccess: false,
          isError: true,
          error: errorMock(
            'postCustomer',
            {
              status: postCustomerError.status,
              statusText: getErrorStatusText(postCustomerError.status, errorCode),
              url: '',
            },
            backendError,
            errorCode,
            backendMessage
          ),
        },
      });
    } else if (isSuccessPostCustomer) {
      setCustomerFlow({
        ...customerFlow,
        resetPatchAndPost,
        customerInfo: {
          isError: false,
          isSuccess: false,
          isLoading: true,
          data: undefined,
          error: undefined,
        },
        postCustomer: {
          ...customerFlow.postCustomer,
          data: postCustomerData,
          isLoading: false,
          isSuccess: true,
          isError: false,
          error: undefined,
        },
      });
      setCustomerId(postCustomerData.id);
    }
  }, [isSuccessPostCustomer, isErrorPostCustomer, isLoadingPostCustomer]);

  useEffect(() => {
    if (isLoadingPatchCustomer) {
      const alreadyDoneAssociateCustomer = getFromLocalStorageByKey('associateCustomer');
      setCustomerFlow({
        ...customerFlow,
        resetPatchAndPost,
        ...(alreadyDoneAssociateCustomer && {
          associateCustomer: {
            ...customerFlow.associateCustomer,
            isSuccess: false,
          },
        }),
        patchCustomer: {
          ...customerFlow.patchCustomer,
          isLoading: true,
          isSuccess: false,
          isError: false,
          error: undefined,
        },
      });
    } else if (isErrorPatchCustomer) {
      const headerErrorCode = patchCustomerError?.headers?.get('ERR-errorCode') || '';
      const backendError = patchCustomerError?.headers?.get('ERR-backendErrorCode') || '';
      const backendMessage = patchCustomerError.headers?.get('ERR-backendErrorMessage') || '';
      const errorCode =
        headerErrorCode === ErrorCodes.DIGITAL_CRM_CODE
          ? handleCRMErrorCodes(backendError)
          : headerErrorCode;
      LoggerInstance.log('PatchCustomer', headerErrorCode, backendError, backendMessage, errorCode);
      setCustomerFlow({
        ...customerFlow,
        resetPatchAndPost,
        patchCustomer: {
          ...customerFlow.patchCustomer,
          isLoading: false,
          isSuccess: false,
          isError: true,
          error: errorMock(
            'patchCustomer',
            {
              status: patchCustomerError.status,
              statusText: getErrorStatusText(patchCustomerError.status, errorCode),
              url: '',
            },
            backendError,
            errorCode,
            backendMessage
          ),
        },
      });
    } else if (isSuccessPatchCustomer) {
      setCustomerFlow({
        ...customerFlow,
        resetPatchAndPost,
        ...(!isAlreadyDoneAssociateCustomer() && {
          associateCustomer: {
            data: undefined,
            isLoading: true,
            isSuccess: false,
            isError: false,
            error: undefined,
          },
        }),
        ...(isAlreadyDoneAssociateCustomer() && {
          associateCustomer: {
            ...customerFlow.associateCustomer,
            isSuccess: true,
          },
        }),
        patchCustomer: {
          ...customerFlow.patchCustomer,
          data: patchCustomerData,
          isLoading: false,
          isSuccess: true,
          isError: false,
          error: undefined,
        },
        customerInfo: {
          ...customerFlow.customerInfo,
          data: patchCustomerData?.[0],
          isLoading: false,
          isSuccess: true,
          isError: false,
          error: undefined,
        },
      });
      localStorage.setItem('customerData', JSON.stringify([patchCustomerData]));
      if (!isAlreadyDoneAssociateCustomer()) {
        associateCustomerMutate();
      }
    }
  }, [isSuccessPatchCustomer, isErrorPatchCustomer, isLoadingPatchCustomer]);

  useEffect(() => {
    if (isLoadingFindcaller) {
      setCustomerFlow({
        ...customerFlow,
        resetPatchAndPost,
        findcaller: {
          ...customerFlow.findcaller,
          isLoading: true,
          isSuccess: false,
          isError: false,
          error: undefined,
        },
      });
    } else if (isErrorFindcaller) {
      setCustomerFlow({
        ...customerFlow,
        resetPatchAndPost,
        findcaller: {
          ...customerFlow.findcaller,
          isLoading: false,
          isSuccess: false,
          isError: true,
          error: errorMock('findcaller', findcallerError),
        },
      });
    } else if (isSuccessFindcaller) {
      setCustomerFlow({
        ...customerFlow,
        resetPatchAndPost,
        customerInfo: {
          isError: false,
          isSuccess: false,
          isLoading: true,
          data: undefined,
          error: undefined,
        },
        findcaller: {
          ...customerFlow.findcaller,
          data: findcallerData,
          isLoading: false,
          isSuccess: true,
          isError: false,
          error: undefined,
        },
      });
      if (findcallerData?.customerHasActiveSubs && findcallerData?.customerRef?.id) {
        setCustomerId(findcallerData.customerRef.id);
      }
    }
  }, [isSuccessFindcaller, isErrorFindcaller, isLoadingFindcaller]);

  const resetPatchAndPost = (stateCustomerFlow: IUseCustomerResponse) => {
    if (stateCustomerFlow?.postCustomer?.isError) {
      localStorage.removeItem('postCustomer');
      setCustomerFlow({
        ...stateCustomerFlow,
        resetPatchAndPost,
        postCustomer: {
          error: undefined,
          data: undefined,
          isLoading: false,
          isError: false,
          isSuccess: false,
        },
      });
    } else if (stateCustomerFlow?.patchCustomer.isError) {
      localStorage.removeItem('patchCustomer');
      setCustomerFlow({
        ...stateCustomerFlow,
        resetPatchAndPost,
        patchCustomer: {
          error: undefined,
          data: undefined,
          isLoading: false,
          isError: false,
          isSuccess: false,
        },
      });
    }
  };

  const createCustomer = () => {
    const cfData: IFiscalCodeReverse | null = computeInverse(
      owningData.owningIndividual.fiscalCode
    ) as IFiscalCodeReverse;
    postCustomerMutate({
      owningData,
      cfInverse: cfData,
    });
  };

  const checkPrecondition = () => {
    if (isNextActive) {
      refetchFindcaller();
    } else if (isLogged) {
      if (!isAlreadyDoneAssociateCustomer()) {
        const customerLocal: GetCustomerResponse = getFromLocalStorageByKey('customerData')?.[0];
        setCustomerFlow({
          ...customerFlow,
          resetPatchAndPost,
          associateCustomer: {
            isLoading: true,
            isSuccess: false,
            isError: false,
            data: undefined,
            error: undefined,
          },
          customerInfo: {
            ...customerFlow.customerInfo,
            data: customerLocal,
            isLoading: false,
            isSuccess: true,
            isError: false,
            error: undefined,
          },
        });
        associateCustomerMutate();
      }
    } else if (currentStepKey !== ID_FLOWS.CONTACT_CARD) {
      const isNextActiveInactive =
        getCurrentUserType() === IUserType.NEXT_USER_INACTIVE ||
        getCurrentUserType() === IUserType.NEXT_USER_ACTIVE;
      const alreadyDonePostCustomer = getFromLocalStorageByKey('postCustomer');
      if (alreadyDonePostCustomer || isNextActiveInactive) {
        patchCustomerMutate(getPatchPayload(owningData));
      } else {
        createCustomer();
      }
    }
  };

  useEffect(() => {
    if (isStartCustomerFlow) {
      checkPrecondition();
      setIsStartCustomerFlow(false);
    }
  }, [isStartCustomerFlow]);

  useEffect(() => {
    setCustomerFlow({
      ...customerFlow,
      resetPatchAndPost,
    });
  }, []);

  return null;
};

